import api from '@/api';
import { isEmpty } from 'lodash';

import { setStoreData, getStoreData } from '@/lib/IndexedDB';
import { notification } from 'antd';

import {
    DEFAULT_TREE_ITEM,
    DEFAULT_DEV_OBJ_PANEL_SETTINGS,
    DEFAULT_APE_PANELS_SETTINGS,
    STORE_NAME,
    STORE_KEY,
} from '@/components/GTMEfficiency/constants';

import staticTreeData from '@/components/GTMEfficiency/Sidebar/CalculationResults/staticData';
import createTree from '@/components/GTMEfficiency/Sidebar/CalculationResults/lib';

const gtmEfficiencyModel = {
    state: {
        loading: false,
        userGtmSettingsLoading: false,
        error: false,
        userGtmSettingsError: false,
        userGtmSettingsData: { fields: [], gtm72: [] },
        isSettingsApplied: false,
        settingsWidgetTitle: '',
        selectedResultsTreeItem: {
            code: DEFAULT_TREE_ITEM,
            key: DEFAULT_TREE_ITEM,
            path: DEFAULT_TREE_ITEM.split('.'),
        },
        developmentObjectPanelSettings: DEFAULT_DEV_OBJ_PANEL_SETTINGS,
        lootVersionsList: [],
        treeData: [],
        selectedRow: null,
        selectedAPEElementNumber: null,
        selectedGeneralAPEElementNumber: null,
        APEActionSettings: DEFAULT_APE_PANELS_SETTINGS,
        userGtmList: [],
        userGtmSelected: [],
        fieldId: null,
        ventureId: null,
        userGtmWellData: null,
        fileUuid: null,
    },
    reducers: {
        setFileUuid(state, payload) {
            return { ...state, fileUuid: payload };
        },

        setLoading(state, payload) {
            return { ...state, loading: payload };
        },
        setUserGtmSettingsLoading(state, payload) {
            return { ...state, userGtmSettingsLoading: payload };
        },
        setError(state, payload) {
            return { ...state, error: payload };
        },
        setUserGtmSettingsError(state, payload) {
            return { ...state, userGtmSettingsError: payload };
        },
        setUserGtmSettingsData(state, payload) {
            return { ...state, userGtmSettingsData: payload };
        },
        setIsSettingsApplied(state, payload) {
            return { ...state, isSettingsApplied: payload };
        },
        setSettingsWidgetTitle(state, payload) {
            return { ...state, settingsWidgetTitle: payload };
        },
        setSelectedResultsTreeItem(state, payload) {
            return { ...state, selectedResultsTreeItem: payload };
        },
        setDevelopmentObjectPanelSettings(state, payload) {
            return { ...state, developmentObjectPanelSettings: payload };
        },
        setLootVersionsList(state, payload) {
            return { ...state, lootVersionsList: payload };
        },
        setTreeData(state, payload) {
            return { ...state, treeData: payload };
        },
        setSelectedRow(state, payload) {
            return { ...state, selectedRow: payload };
        },
        setSelectedAPEElementNumber(state, payload) {
            return { ...state, selectedAPEElementNumber: payload };
        },
        setSelectedGeneralAPEElementNumber(state, payload) {
            return { ...state, selectedGeneralAPEElementNumber: payload };
        },
        setAPEActionSettings(state, payload) {
            return { ...state, APEActionSettings: payload };
        },
        setAPEDetailPanelChartSettings(state, payload) {
            const { APEActionSettings } = state;
            const { detailPanel } = APEActionSettings;

            return {
                ...state,
                APEActionSettings: { ...APEActionSettings, detailPanel: { ...detailPanel, chartSettings: payload } },
            };
        },
        removeSelectedUserGtmReducer(state, payload) {
            const uuids = state.userGtmSelected;
            return {
                ...state,
                userGtmList: state.userGtmList.filter(e => uuids.indexOf(e.uuid) === -1),
            };
        },
        removeAllUserGtmReducer(state, payload) {
            const uuids = state.userGtmSelected;

            if (payload?.ignoreIfmatсh) {
                if (payload.ignoreIfmatсh.field == state.fieldId && payload.ignoreIfmatсh.venture == state.ventureId) {
                    return state;
                }
            }

            return {
                ...state,
                userGtmList: [],
            };
        },
        setSelectedUserGtmReducer(state, payload) {
            return {
                ...state,
                userGtmSelected: payload,
            };
        },
        setLocationReducer(state, payload) {
            return {
                ...state,
                fieldId: payload.fieldId,
                ventureId: payload.ventureId,
            };
        },
        addUserGtmReducer(state, payload) {
            return {
                ...state,
                userGtmList: [...state.userGtmList.filter(e => e.uuid !== payload.uuid), payload],
            };
        },
        addUserGtmsReducer(state, payload) {
            return {
                ...state,
                userGtmList: [...state.userGtmList.filter(e => e.uuid !== payload.uuid), ...payload],
            };
        },
        setUserGtmWellDataReducer(state, payload) {
            return {
                ...state,
                userGtmWellData: payload,
            };
        },
        setUserGtmList(state, payload) {
            return {
                ...state,
                userGtmList: payload,
            };
        },
        resetCalculationState(state) {
            return {
                ...state,
                isSettingsApplied: false,
                settingsWidgetTitle: '',
                selectedResultsTreeItem: {
                    code: DEFAULT_TREE_ITEM,
                    key: DEFAULT_TREE_ITEM,
                    path: DEFAULT_TREE_ITEM.split('.'),
                },
                treeData: [],
                selectedRow: null,
                selectedAPEElementNumber: null,
                selectedGeneralAPEElementNumber: null,
                APEActionSettings: DEFAULT_APE_PANELS_SETTINGS,
                developmentObjectPanelSettings: DEFAULT_DEV_OBJ_PANEL_SETTINGS,
                fileUuid: null,
            };
        },
    },
    effects: dispatch => ({
        async fetchUserGtmWellData(params) {
            if (!params.wellId) {
                dispatch.gtmEfficiency.setUserGtmWellDataReducer(null);
                return;
            }
            dispatch.gtmEfficiency.setUserGtmWellDataReducer(null);
            try {
                const response = await api.gtmEfficiency.fetchUserGtmWellData(params);
                dispatch.gtmEfficiency.setUserGtmWellDataReducer(response);
            } catch (error) {
                dispatch.gtmEfficiency.setUserGtmWellDataReducer(null);
            }
        },
        async fetchVersionsAsync(params) {
            dispatch.gtmEfficiency.setLoading(true);

            try {
                const response = await api.gtmEfficiency.fetchVersions(params);

                dispatch.gtmEfficiency.setLootVersionsList(response);
                dispatch.gtmEfficiency.setLoading(false);
            } catch (error) {
                dispatch.gtmEfficiency.setError(error);
                dispatch.gtmEfficiency.setLoading(false);
            }
        },
        async fetchUserGtmSettingsAsync(params) {
            dispatch.gtmEfficiency.setUserGtmSettingsLoading(true);

            try {
                const response = await api.gtmEfficiency.fetchUserGtmSettings(params);

                dispatch.gtmEfficiency.setUserGtmSettingsData(response);
                dispatch.gtmEfficiency.setUserGtmSettingsLoading(false);
                dispatch.gtmEfficiency.setUserGtmSettingsError(false);
            } catch (error) {
                dispatch.gtmEfficiency.setUserGtmSettingsError(error);
                dispatch.gtmEfficiency.setUserGtmSettingsLoading(false);
            }
        },
        // TODO: move to tree model?
        async fetchCalculationAsync(params) {
            dispatch.gtmEfficiency.setLoading(true);

            try {
                const { uuid } = await api.gtmEfficiency.fetchCalculationId(JSON.stringify(params));

                dispatch.gtmEfficiency.setFileUuid(uuid);
                let response = null;

                const timerId = setInterval(async () => {
                    response = await api.gtmEfficiency.fetchCalculationData(JSON.stringify({ uuid }));

                    if (response.success) {
                        clearInterval(timerId);

                        const { url, urlTree } = response;

                        const dynamicTreeData = await api.gtmEfficiency.fetchCalculationTreeFile(urlTree);
                        const treeData = createTree({ staticData: staticTreeData, dynamicData: dynamicTreeData });
                        dispatch.gtmEfficiency.setTreeData(treeData);

                        const calcData = await api.gtmEfficiency.fetchCalculationDataFile(url);

                        await setStoreData({ name: STORE_NAME, value: calcData, key: STORE_KEY });

                        dispatch.gtmEfficiency.setIsSettingsApplied(true);
                        dispatch.gtmEfficiency.setLoading(false);
                    }
                    if (response.hasError) {
                        clearInterval(timerId);

                        notification.error({
                            message: 'Ошибка при выполнении расчета',
                            // description: errorTypesDescriptions[type],
                        });
                        dispatch.gtmEfficiency.setError('Ошибка при выполнении расчета');
                        dispatch.gtmEfficiency.setLoading(false);
                    }
                }, 5000);
            } catch (error) {
                dispatch.gtmEfficiency.setError(error);
                dispatch.gtmEfficiency.setLoading(false);
            }
        },
        async getIndexedDBDataAsync() {
            try {
                const data = await getStoreData({ name: STORE_NAME, key: STORE_KEY });
                return data;
            } catch (error) {
                return {};
            }
        },
        async saveUserGtmList(_, rootState) {
            try {
                await api.user.saveData({ key: 'userGtm', data: rootState.gtmEfficiency.userGtmList });
            } catch (error) {
                console.log(error);
            }
        },
        async getUserGtmList() {
            try {
                const response = await api.user.loadData('userGtm');
                if (!isEmpty(response.data)) {
                    dispatch.gtmEfficiency.setUserGtmList(response.data);
                    return;
                }
                dispatch.gtmEfficiency.setUserGtmList([]);
            } catch (error) {
                dispatch.gtmEfficiency.setUserGtmList([]);
                console.error('getUserGtmList', error);
            }
        },
        removeSelectedGtmFromList() {
            dispatch.gtmEfficiency.removeSelectedUserGtmReducer();
        },
        setSelectedUserGtm(uuids) {
            dispatch.gtmEfficiency.setSelectedUserGtmReducer(uuids);
        },
        setLocation({ field, venture }) {
            dispatch.gtmEfficiency.setLocationReducer({ fieldId: field, ventureId: venture });
        },
        addUserGtm(gtm) {
            dispatch.gtmEfficiency.addUserGtmReducer(gtm);
        },
        addUserGtms(gtm) {
            dispatch.gtmEfficiency.addUserGtmsReducer(gtm);
        },
        removeAllUserGtm(payload) {
            dispatch.gtmEfficiency.removeAllUserGtmReducer(payload);
        },
    }),
};

export default gtmEfficiencyModel;
